import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
const MAPBOX_URL = 'https://api.mapbox.com/search/geocode/v6/forward';
// Stable identity so the idle case does not hand Autocomplete a new array each render.
const EMPTY_RESULTS: Array<{ lat: number; lng: number; address: string }> = [];

interface MapboxFeature {
    geometry: { coordinates: [number, number] };
    properties: { full_address: string };
}

interface GeocodingProps {
    onLocationSelect?: (lat: number, lng: number) => void;
    onAddressChange?: (address: string) => void;
    proximity?: { lat: number; lng: number };
    initialAddress?: string;
}

export const Geocoding: React.FC<GeocodingProps> = ({ onLocationSelect, onAddressChange, proximity, initialAddress }) => {
    const [address, setAddress] = useState(initialAddress ?? '');
    const [results, setResults] = useState<Array<{ lat: number; lng: number; address: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const query = address.trim();
    // Both conditions are derivable from props/state, so they are computed during
    // render instead of being pushed into state from inside the effect.
    const isQueryTooShort = query.length < 3;
    const isTokenMissing = !MAPBOX_TOKEN;
    const isIdle = isQueryTooShort || isTokenMissing;

    useEffect(() => {
        if (isIdle) {
            return;
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(async () => {
            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({
                    language: 'fr',
                    autocomplete: 'true',
                    access_token: MAPBOX_TOKEN!,
                    q: query,
                });

                if (proximity) {
                    params.set('proximity', `${proximity.lng},${proximity.lat}`);
                }

                const response = await fetch(`${MAPBOX_URL}?${params.toString()}`, {
                    signal: controller.signal,
                });
                const data = await response.json();

                if (!data?.features || data.features.length === 0) {
                    setResults([]);
                    return;
                }

                setResults(
                    (data.features as MapboxFeature[]).map((feature) => ({
                        lat: feature.geometry.coordinates[1],
                        lng: feature.geometry.coordinates[0],
                        address: feature.properties.full_address,
                    }))
                );
            } catch (err) {
                if ((err as Error).name !== 'AbortError') {
                    setError('Failed to geocode address');
                }
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => {
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [isIdle, query, proximity]);

    // While idle the stored state is stale (the effect returns without clearing it),
    // so what the user sees is derived rather than stored.
    const visibleResults = isIdle ? EMPTY_RESULTS : results;
    const visibleLoading = isIdle ? false : loading;
    const visibleError = isTokenMissing ? 'Missing MAPBOX_TOKEN' : isQueryTooShort ? null : error;

    const handleSelectResult = (lat: number, lng: number) => {
        onLocationSelect?.(lat, lng);
    };

    return (
        <div className="geocoding-container">
            <Autocomplete<{ lat: number; lng: number; address: string }, false, false, true>
                freeSolo
                className="search"
                loading={visibleLoading}
                options={visibleResults}
                noOptionsText={address.trim().length < 3 ? 'Type at least 3 characters' : 'No results'}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.address
                }
                inputValue={address}
                onInputChange={(_, value) => {
                    setAddress(value);
                    onAddressChange?.(value);
                }}
                onChange={(_, value) => {
                    if (value && typeof value !== 'string') {
                        setAddress(value.address);
                        onAddressChange?.(value.address);
                        handleSelectResult(value.lat, value.lng);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Adresse"
                        placeholder="Tapez au moins 3 caractères..."
                        size="small"
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...params.slotProps.input,
                                endAdornment: (
                                    <>
                                        {visibleLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                        {params.slotProps.input.endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
                sx={{ flex: 1 }}
            />

            {visibleError && <p className="error">{visibleError}</p>}

        </div>
    );
};